import { Close as CloseIcon } from "@mui/icons-material";
import { Box, Button, IconButton } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";

import { monospaceStyle } from "../../theme";
import { UNNAMED_PRODUCT } from "../Explore/productFields";
import { MAX_COMPARED, compareAddress } from "./compareState";
import { useCompareTray } from "./compareTray";

// The fewest products a comparison needs
const MIN_COMPARED = 2;

/**
 * The bar of the products picked for comparison: each as a chip that can be
 * taken out, and the way into the comparison. It renders nothing while the
 * tray is empty. It sticks to the bottom of the scrolling area it is placed
 * at the end of, so that it stays in view without covering the last result.
 */
const CompareTrayBar: React.FC = () => {
  const tray = useCompareTray();
  if (tray.items.length === 0) {
    return null;
  }

  const ready = tray.items.length >= MIN_COMPARED;
  const address = compareAddress(tray.items.map((item) => item.id));

  return (
    <Box
      component="section"
      aria-label="Comparison"
      sx={(theme) => ({
        position: "sticky",
        bottom: 0,
        zIndex: 3,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 1.25,
        px: 1.75,
        py: 1.25,
        backgroundColor: "background.raised",
        border: 1,
        borderColor: "divider",
        borderRadius: `${theme.radius.surface}px`,
        boxShadow: "0 8px 24px rgba(0, 0, 0, 0.45)",
      })}
    >
      <Box
        component="span"
        sx={{
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "text.secondary",
        }}
      >
        Compare
      </Box>
      {tray.items.map((item) => {
        const name = item.name ?? UNNAMED_PRODUCT;
        return (
          <Box
            key={item.id}
            component="span"
            sx={(theme) => ({
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              height: 24,
              pl: "9px",
              pr: "2px",
              borderRadius: `${theme.radius.chip}px`,
              border: "1px solid rgba(255, 255, 255, 0.1)",
              backgroundColor: "rgba(255, 255, 255, 0.03)",
              fontSize: 12.5,
              whiteSpace: "nowrap",
            })}
          >
            {name}
            <IconButton
              size="small"
              aria-label={`Remove ${name}`}
              onClick={() => tray.remove(item.id)}
              sx={{ p: "2px", color: "text.disabled" }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        );
      })}
      <Box
        component="span"
        sx={{ ...monospaceStyle, fontSize: 12, color: "text.secondary" }}
      >
        {tray.items.length} of {MAX_COMPARED}
      </Box>
      <Box sx={{ flex: 1 }} />
      {!ready && (
        <Box component="span" sx={{ fontSize: 12, color: "text.disabled" }}>
          pick one more
        </Box>
      )}
      <Button size="small" onClick={tray.clear}>
        Clear
      </Button>
      {ready ? (
        <Button size="small" variant="contained" component={Link} to={address}>
          Compare
        </Button>
      ) : (
        <Button size="small" variant="contained" disabled>
          Compare
        </Button>
      )}
    </Box>
  );
};

export default CompareTrayBar;
